import { getExchangeNow } from '@/lib/exchange-time'
import type { TimeSliceValue } from '../schemas/filter-entity'

/**
 * Resolves a time-slice filter's array of `{start, end, value}` windows to a
 * single scalar value based on the current exchange-tz clock.
 *
 * Semantics (mirrors legacy `resolveSliceGroup` at
 * edge/src/app/slicedForm/mapping/profileToQuery.js:459):
 *
 *  1. Walk slices in order. The first slice whose window contains `now` wins
 *     (inclusive start, exclusive end).
 *  2. If no slice contains `now` (outside 04:00–20:00, or inside a
 *     user-created gap), fall back to the **last** slice's value.
 *  3. Return `null` when the slice array is empty, or when the winning
 *     slice's value itself is `null` (user left the threshold blank).
 *
 * The caller at `profileToQuery.buildLeafFilter` drops the leaf when this
 * returns `null` — same silent-drop policy as the other validators.
 *
 * @param now Date whose `.getHours()` / `.getMinutes()` are in exchange tz.
 *            Defaults to `getExchangeNow()`. Tests pass a controllable Date.
 */
export function resolveTimeSlice(
  slices: TimeSliceValue,
  now: Date = getExchangeNow(),
): number | null {
  if (slices.length === 0) return null

  const nowMinutes = now.getHours() * 60 + now.getMinutes()

  for (const slice of slices) {
    const startMinutes = hhmmToMinutes(slice.start)
    const endMinutes = hhmmToMinutes(slice.end)
    if (nowMinutes >= startMinutes && nowMinutes < endMinutes) {
      return slice.value
    }
  }

  // Fallback: outside every window → use the last slice's value. Matches
  // legacy's last-slice-is-default behavior for pre-04:00 and post-20:00.
  return slices[slices.length - 1].value
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}
